import React, { createContext, useState, useContext, useEffect, useRef } from "react";

// Create Context
const SolutionDropdownContext = createContext();

// Provider Component
export const SolutionDropdownProvider = ({ children }) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const dropdownRef = useRef(null);
  const closeTimeoutRef = useRef(null);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Toggle dropdown visibility
  const toggleDropdown = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    
    if (isMobile) {
      setIsDropdownOpen(true);
      setIsMenuOpen(true);
    } else {
      setIsDropdownOpen(prev => !prev);
    }
  };

  // Close both dropdown and mobile menu
  const closeDropdown = () => {
    setIsDropdownOpen(false);
  };

  // Start close timer
  const startCloseTimer = () => {
    if (!isMobile) {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
      closeTimeoutRef.current = setTimeout(() => {
        closeDropdown();
      }, 1000);
    }
  };

  // Cancel close timer
  const cancelCloseTimer = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  };

  // Toggle the mobile menu
  const toggleMenu = () => {
    setIsMenuOpen(prev => !prev);
    if (!isMenuOpen) {
      setIsDropdownOpen(false);
    }
  };

  // Close the entire menu and dropdown
  const closeMenu = () => {
    setIsMenuOpen(false);
    setIsDropdownOpen(false);
  };

  // Handle clicks outside the dropdown to close it
  const handleClickOutside = (event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      closeDropdown();
      if (isMobile) {
        closeMenu();
      }
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
    };
  });

  return (
    <SolutionDropdownContext.Provider
      value={{ 
        isDropdownOpen, 
        toggleDropdown, 
        closeDropdown, 
        isMenuOpen, 
        toggleMenu, 
        closeMenu, 
        dropdownRef,
        isMobile,
        startCloseTimer,
        cancelCloseTimer
      }}
    >
      {children}
    </SolutionDropdownContext.Provider>
  );
};

// Custom hook to use the SolutionDropdownContext
export const useSolutionDropdown = () => {
  const context = useContext(SolutionDropdownContext);
  if (!context) {
    throw new Error("useSolutionDropdown must be used within a SolutionDropdownProvider");
  }
  return context;
};
